import React, {
    useMemo,
    useState,
    useEffect,
    useLayoutEffect,
    useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import filterActions from '@app/actions/filters';

import { Formik } from 'formik';

import * as R from 'ramda';

import SelectedFiltersTags from './components/SelectedFiltersTags';

import connect from '@app/utils/connect';
import sportsApi from '@app/utils/sportsApi';
import camelToSnakeCase from '@app/utils/camelToSnake';
import dayjs from 'dayjs';
import './Filters.scss';
import FiltersForm from './components/FiltersForm';
import { FILTER_ORIGIN } from '@app/constants/filters';
import compareFilters from '@app/utils/compareFilters';
import usePendo from '@app/components/hooks/usePendo';
import { shortDate } from '@app/utils/formatters';
import {
    SMV_RSP_BLUEPRINT_ID,
    SMV_RS_BLUEPRINT_NAME,
    MVP_BLUEPRINT_ID,
    INSIGHT_TYPES_OPTIONS,
} from '@app/components/automated-insights/constants';

import './components/Filters.scss';

import {
    getSelected,
    getRHLeagueNames,
    getFilterValueNames,
    getPlacementNames,
    getPlatformsNames,
    getGroupByNames,
    getSortBy,
    getComparedDate,
    parseSelectedPlacements,
    getSelectedNestedRightsholders,
    setSelected,
} from '@app/utils/filterFormHelpers';
import { parseStartDate, parseEndDate } from '@app/utils/parseDate';
import {
    SOCIAL_PLATFORMS_OPTIONS,
    BROADCAST_PLATFORM_OPTIONS,
    GAME_PLATFORM_OPTIONS,
} from '@app/insights/constants';
import { BROADCAST, IN_VENUE } from '@app/constants/platformIds';
import { greaterThanOptions } from '@app/social-media-posts/dropdownOptions';
import getSelectedGroupByOptions from '@app/utils/getSelectedGroupByOptions';
import parseStoredGroupBy from '@app/utils/parseStoredGroupBy';
import { expandPlacementOptions } from '@app/utils/filtersHelperFunctions';

import { getPlacementOptions } from './getSpecificOptions';

import { useAutomatedInsightsContext } from '@app/components/automated-insights/context';

import { Collapse, ConfigProvider, Flex, matchesDateRange } from '@app/ui';
import { useParams } from 'react-router-dom';
import { BroadcastTab } from '@app/broadcasts/utils/broadcastsUtils';
import {
    AGE_BRACKET_LABEL_KEYS,
    GENDER_LABEL_KEYS,
} from '@app/constants/demographics';
import {
    PREMIUM_PLACEMENT_TYPES,
    EDGE_RIGHTSHOLDER_ATTRIBUTION,
} from '@app/constants/config';
import useAppLayoutHeaderBottom, {
    getAppLayoutHeaderBottom,
} from '@app/components/hooks/useAppLayoutHeaderBottom';

const formatStr = 'YYYY-MM-DDTHH:mm:ss.SSSZ'; // Day.js format

const ReFilters = ({
    client,
    saveFilters,
    filters,
    broadcasts,
    media,
    user,
    origin,
    isDefaultOpen = false,
    getFilters,
    sendNotifications,
    clearOverrideFilters,
    isOverridingFilters,
}) => {
    const { activeTab: currentTab } = useParams();
    const { t } = useTranslation();

    const showDefaultFilters = origin === FILTER_ORIGIN.SETTINGS;
    const shouldHideArrow = origin === FILTER_ORIGIN.SETTINGS;
    const [activeKey, setActiveKey] = useState(isDefaultOpen ? ['1'] : []);

    const stickyRef = useRef<HTMLDivElement | null>(null);
    const layoutHeaderBottom = useAppLayoutHeaderBottom();
    const lastStuckClassRef = useRef<boolean | null>(null);

    // Drive sticky visuals on the DOM (no React state) so the filters and any consumer
    // (e.g. the SMP list-view table header) stay in lockstep with native `position: sticky`.
    // We expose three CSS variables on `<html>` that the table uses to position itself:
    //   --app-header-bottom         live bottom edge of the app header
    //   --filters-sticky-offset     extra space below the header to clear the stuck filters
    //   --filters-sticky-gap-top    where the gap mask between filters and table sits
    useLayoutEffect(() => {
        const root = document;
        const STUCK_CLASS = 'filters-sticky-active';

        const syncStuckClass = () => {
            const el = stickyRef.current as HTMLElement | null;
            if (!el) return;

            const headerBottom = getAppLayoutHeaderBottom();
            const top = el.getBoundingClientRect().top;
            const stuck = top <= headerBottom + 0.5;

            // Header height can change on scroll on some pages — keep this CSS var fresh.
            document.documentElement.style.setProperty(
                '--app-header-bottom',
                `${headerBottom}px`,
            );

            if (lastStuckClassRef.current === stuck) return;
            lastStuckClassRef.current = stuck;
            el.classList.toggle(STUCK_CLASS, stuck);

            const filtersHeight = el.offsetHeight;
            document.documentElement.style.setProperty(
                '--filters-sticky-offset',
                stuck ? `${filtersHeight + 12}px` : '0px',
            );
            document.documentElement.style.setProperty(
                '--filters-sticky-gap-top',
                stuck ? `${headerBottom + filtersHeight}px` : '0px',
            );
            document.documentElement.style.setProperty(
                '--filters-sticky-gap-visible',
                stuck ? '12px' : '0px',
            );
        };

        syncStuckClass();

        root.addEventListener('scroll', syncStuckClass, {
            passive: true,
            capture: true,
        });
        window.addEventListener('resize', syncStuckClass);

        const resizeObserver = new ResizeObserver(syncStuckClass);
        const observeTarget = () => {
            const el = stickyRef.current;
            if (el) resizeObserver.observe(el);
        };
        observeTarget();
        const observeRaf = requestAnimationFrame(observeTarget);

        return () => {
            cancelAnimationFrame(observeRaf);
            (stickyRef.current as HTMLElement | null)?.classList.remove(
                STUCK_CLASS,
            );
            lastStuckClassRef.current = null;
            root.removeEventListener('scroll', syncStuckClass, true);
            window.removeEventListener('resize', syncStuckClass);
            resizeObserver.disconnect();
            const html = document.documentElement.style;
            html.removeProperty('--filters-sticky-offset');
            html.removeProperty('--filters-sticky-gap-top');
            html.removeProperty('--filters-sticky-gap-visible');
        };
    }, [layoutHeaderBottom]);

    const {
        rightsholdersLeaguePairs = {},
        isLoading,
        shouldShowLeagueHierarchy,
        platformPostTypePairs = {},
        accOwnTypes = {},
        mediaTypes = {},
        collections = {},
        hashtags,
        captions,
        accountUsernames,
        mvpScore,
        authorshipTypes = [],
        collaboratingAccountUsernames = [],
        broadcastNames,
        broadcastOptions,
        insightsGroupBy = [],
        sort,
        order,
        placementTypes = {},
        hasSeveralRightsholders,
        sponsors = {},
        rightsholders = {},
        placements = {},
        startDate,
        endDate,
        dateSpec,
        userDefaultFilters: {
            filterJson: { dateRange: defaultDateRange = null } = {},
        } = {},
        compareDates,
        countries = {},
        broadcastNetworks = {},
        dateRange: savedDateRange,
        ageBrackets = [],
        genders = [],
    } = filters;

    // Get overridingFiltersElement from the appropriate store section based on origin
    const overridingFiltersElement =
        origin === FILTER_ORIGIN.SMP
            ? media?.overridingFiltersElement || []
            : broadcasts?.overridingFiltersElement || [];

    const dateRange = savedDateRange || defaultDateRange;

    const { pendo } = usePendo();

    const selectedRhsLeagues = useMemo(() => {
        if (!shouldShowLeagueHierarchy) return [];
        return rightsholdersLeaguePairs;
    }, [rightsholdersLeaguePairs, shouldShowLeagueHierarchy]);

    const selectedStartDate = useMemo(
        () => (startDate ? new Date(startDate) : undefined),
        [filters],
    );

    const selectedEndDate = useMemo(
        () => (endDate ? new Date(endDate) : undefined),
        [filters],
    );

    const plcOptions = getPlacementOptions({
        plcList: placements.list,
        rhList: rightsholders.list,
        hasSeveralRightsholders,
    });

    const selectedPlc = parseSelectedPlacements(
        placements.selected,
        plcOptions,
        hasSeveralRightsholders,
    );

    const { filterTypes: insightsIds, setFilterTypes: setInsightsIds } =
        useAutomatedInsightsContext();

    useEffect(() => {
        if (!showDefaultFilters) return;
        getFilters();
    }, []);

    // Helper function to get selected values from filter objects
    const selectedFilters = filter => setSelected(filter);

    // Check if broadcasts or in-venue should be shown
    const showBroadcast = client.permissions?.includes(BROADCAST) || false;
    const showInVenue = client.permissions?.includes(IN_VENUE) || false;

    // Filter persistence effect - Maintains RSP and date filters across pages
    useEffect(() => {
        // Persists RSP and date filters into homepage
        // And set all other filters to null
        if (showDefaultFilters) return;

        const isFilterOriginInsights = origin === FILTER_ORIGIN.INSIGHTS;
        const isOverridingFilters =
            isFilterOriginInsights && filters.isReturningFromOverride;

        // Get default platforms based on grouping by type
        const defaultPlatformsWhenGroupingBy = (() => {
            if (insightsGroupBy.includes('socialAccountGroup'))
                return getSelected(SOCIAL_PLATFORMS_OPTIONS);
            if (insightsGroupBy.includes('mediaUploadName'))
                return getSelected(BROADCAST_PLATFORM_OPTIONS);
            if (insightsGroupBy.includes('gameName'))
                return getSelected(
                    GAME_PLATFORM_OPTIONS(showBroadcast, showInVenue),
                );
            return null;
        })();

        const shouldShowDefaultPlatforms =
            isFilterOriginInsights &&
            !isOverridingFilters &&
            defaultPlatformsWhenGroupingBy;

        // Helper to return selected values or an empty array based on overriding condition
        const getFilterValues = filter =>
            isOverridingFilters ? getSelected(selectedFilters(filter)) : [];

        const homeFilters = {
            rightsholders: getSelected(selectedFilters(rightsholders)),
            rightsholdersLeaguePairs: selectedRhsLeagues,
            sponsors: getSelected(selectedFilters(sponsors)),
            placements: parseSelectedPlacements(
                placements.selected,
                plcOptions,
                hasSeveralRightsholders,
            ),
            placementTypes: getSelected(selectedFilters(placementTypes)),
            startDate: selectedStartDate && parseStartDate(selectedStartDate),
            endDate: selectedEndDate && parseEndDate(selectedEndDate),
            dateSpec,
            platformPostTypePairs: shouldShowDefaultPlatforms
                ? defaultPlatformsWhenGroupingBy
                : getFilterValues(platformPostTypePairs),
            accOwnTypes: getFilterValues(accOwnTypes),
            mediaTypes: getFilterValues(mediaTypes),
            collections: getFilterValues(collections),
            hashtags: isOverridingFilters ? hashtags : [],
            captions: isOverridingFilters ? captions : [],
            accountUsernames: isOverridingFilters ? accountUsernames : [],
            authorshipTypes: [],
            collaboratingAccountUsernames: [],
            audiencePlatforms: [],
            networks: [],
            mvpScore: 0,
            broadcastNames: [],
            broadcastOptions: [],
            countries: getFilterValues(countries),
            broadcastNetworks: [],
            ageBrackets: [],
            genders: [],
            insightsGroupBy,
            sort,
            order,
            compareDates,
            dateRange,
            ...(shouldShowLeagueHierarchy
                ? {
                      rightsholders:
                          getSelectedNestedRightsholders(selectedRhsLeagues),
                  }
                : {}),
        };
        saveFilters(homeFilters);
    }, [showDefaultFilters]);

    const [selectedFilterTagsData, setSelectedFilterTagsData] = useState(null);

    useEffect(() => {
        (async () => {
            if (!isLoading) {
                //Find RH/Nested Rightsholders Names
                let foundRHNames = [];
                if (shouldShowLeagueHierarchy) {
                    const rhNamesFromPairs = getRHLeagueNames(
                        rightsholdersLeaguePairs,
                        rightsholders,
                    );
                    const rhNamesFromSelected =
                        getFilterValueNames(rightsholders);
                    // Keep tags aligned with actual selected RH ids while still
                    // supporting league-pair derived labels.
                    foundRHNames = [
                        ...new Set([
                            ...rhNamesFromPairs,
                            ...rhNamesFromSelected,
                        ]),
                    ];
                } else {
                    foundRHNames = getFilterValueNames(rightsholders);
                }

                // Find Sponsors Names
                const foundSpNames = getFilterValueNames(sponsors);

                // Find Placement Types Names
                const foundPlTypeNames = getFilterValueNames(placementTypes);

                // Find Placement Names
                const foundPlNames = getPlacementNames({
                    options: plcOptions,
                    selected: selectedPlc,
                    hasSeveralRightsholders,
                });

                const foundPltNames = getPlatformsNames(platformPostTypePairs);

                const foundAccNames = getFilterValueNames(accOwnTypes);

                // Find Media Types Names
                const foundMediaTypesNames = getFilterValueNames(mediaTypes);

                // Find Collections Names
                const foundCollectionsNames = getFilterValueNames(collections);

                // Find countries Names
                const foundCountriesNames = getFilterValueNames(countries);

                const foundBroadcastNetworksNames =
                    getFilterValueNames(broadcastNetworks);

                // Get MVP Score Name
                const mvpScoreName =
                    origin === FILTER_ORIGIN.SMP
                        ? [
                              greaterThanOptions.find(o => o.value === mvpScore)
                                  ?.name,
                          ]
                        : [];

                // Get Insights group By Names
                const groupByNames = await getGroupByNames(
                    insightsGroupBy,
                    !!compareDates?.option,
                );

                const comparedDateName = getComparedDate({
                    startDate,
                    endDate: endDate || new Date(),
                    compareDates,
                });

                const sortByName = getSortBy({
                    sort,
                    order,
                });

                // Authorship type display names (standard, collaboration, unsupported -> translated labels)
                const authorshipTypeLabelMap = {
                    standard: t('filters.authorshipType.standard'),
                    collaboration: t('filters.authorshipType.collaboration'),
                    unsupported: t('filters.authorshipType.unsupported'),
                };
                const foundAuthorshipTypesNames = Array.isArray(authorshipTypes)
                    ? authorshipTypes
                          .filter(k => k in authorshipTypeLabelMap)
                          .map(k => authorshipTypeLabelMap[k])
                    : [];

                // Collaborating accounts: usernames as-is for display
                const foundCollaboratingAccountUsernamesNames = Array.isArray(
                    collaboratingAccountUsernames,
                )
                    ? collaboratingAccountUsernames
                    : [];

                setSelectedFilterTagsData({
                    rhNames: foundRHNames,
                    spNames: foundSpNames,
                    plNames: foundPlNames,
                    plTypeNames: foundPlTypeNames,
                    pltNames: foundPltNames,
                    accNames: foundAccNames,
                    mediaTypesNames: foundMediaTypesNames,
                    collectionsNames: foundCollectionsNames,
                    countriesNames: foundCountriesNames,
                    broadcastNetworksNames: foundBroadcastNetworksNames,
                    startDate: startDate ? [shortDate(startDate)] : [],
                    endDate: endDate ? [shortDate(endDate)] : [],
                    mvpScoreName: mvpScoreName,
                    insightsGroupBy: groupByNames,
                    sortBy: [sortByName],
                    compareDates: comparedDateName ? [comparedDateName] : [],
                    insightsIds,
                    authorshipTypesNames: foundAuthorshipTypesNames,
                    collaboratingAccountUsernamesNames:
                        foundCollaboratingAccountUsernamesNames,
                    captions,
                });
            } else {
                setSelectedFilterTagsData({
                    rhNames: [],
                    spNames: [],
                    plNames: [],
                    plTypeNames: [],
                    pltNames: [],
                    accNames: [],
                    mediaTypesNames: [],
                    collectionsNames: [],
                    countriesNames: [],
                    broadcastNetworksNames: [],
                    startDate: [],
                    endDate: [],
                    mvpScoreName: [],
                    insightsGroupBy: [],
                    sortBy: [],
                    compareDates: [],
                    insightsIds: [],
                    authorshipTypesNames: [],
                    collaboratingAccountUsernamesNames: [],
                    captions: [],
                });
            }
        })();
    }, [
        sponsors,
        rightsholders,
        placements,
        isLoading,
        shouldShowLeagueHierarchy,
        showDefaultFilters,
        insightsGroupBy,
        sort,
        order,
        compareDates,
        insightsIds,
        authorshipTypes,
        collaboratingAccountUsernames,
        captions,
        ageBrackets,
        genders,
        t, // Re-run when language changes to update translated filter names
    ]);

    const handleSubmit = async values => {
        const shouldSaveDefault = origin === FILTER_ORIGIN.SETTINGS;

        try {
            if (origin === FILTER_ORIGIN.AUTO_INSIGHTS) {
                const { insightsIds } = values;
                let newInsightsIds = [];

                if (insightsIds.includes('smv')) {
                    newInsightsIds = [
                        ...newInsightsIds,
                        SMV_RS_BLUEPRINT_NAME,
                        SMV_RSP_BLUEPRINT_ID,
                    ];
                }

                if (insightsIds.includes('mvp')) {
                    newInsightsIds = [...newInsightsIds, MVP_BLUEPRINT_ID];
                }

                setInsightsIds(newInsightsIds);
            }
            if (shouldSaveDefault) {
                const hasRightsholderAttributionPermission =
                    client?.permissions?.includes(
                        EDGE_RIGHTSHOLDER_ATTRIBUTION,
                    );
                const currentAttributionMode =
                    filters?.rightsholderAttributionMode || 'team';
                const newFilters = R.pick(
                    [
                        'rightsholders',
                        'placements',
                        'sponsors',
                        'startDate',
                        'endDate',
                        'dateRange',
                        'rightsholdersLeaguePairs',
                        'shouldShowLeagueHierarchy',
                    ],
                    values,
                );
                if (hasRightsholderAttributionPermission) {
                    newFilters.rightsholderAttributionMode =
                        currentAttributionMode;
                }

                const { filterJson: previousDefaultFilters, id: filterId } =
                    filters.userDefaultFilters;

                const shouldUpdateDefaultFilters = compareFilters(
                    previousDefaultFilters,
                    filters,
                );

                if (!shouldUpdateDefaultFilters) return;

                const filterIdStr = filterId ? `${filterId}` : '';
                const url = `/users/${user.id}/mvc-global-filters/${filterIdStr}`;

                const domainObject = {
                    filterJson: JSON.stringify(newFilters),
                };
                const call = sportsApi.url(url).json({ domainObject });
                (await filterId) ? call.put() : call.post();
            }

            // Clear override whenever filters are updated from SMP or Broadcast Exposures
            if (
                origin == FILTER_ORIGIN.SMP ||
                origin == FILTER_ORIGIN.BROADCAST_EXPOSURES
            )
                clearOverrideFilters();

            const {
                startDate,
                endDate,
                rightsholders,
                placements,
                placementTypes,
                sponsors,
                insightsGroupBy,
                rightsholdersLeaguePairs,
                mvpScore,
                sort,
                order,
                broadcastNetworks,
                broadcastNames,
                broadcastOptions,
                dateRange,
                ageBrackets,
                genders,
            } = values;

            const saveValues = {
                rightsholders,
                placements: expandPlacementOptions(placements),
                placementTypes,
                sponsors,
                startDate: startDate
                    ? startDate.startOf('day').format(formatStr)
                    : null,
                endDate: endDate
                    ? endDate.endOf('day').format(formatStr)
                    : null,
                dateSpec: values.dateSpec ?? null,
                insightsGroupBy: getSelectedGroupByOptions(
                    insightsGroupBy,
                    !!values?.compareDates?.option,
                ),
                rightsholdersLeaguePairs,
                platformPostTypePairs: getSelected(values.platforms),
                accOwnTypes: getSelected(values.accTypes),
                mediaTypes: values.mediaTypes,
                collections: values.collections,
                hashtags: values.hashtags,
                captions: values.caption ? [values.caption] : [],
                accountUsernames: values.handles,
                authorshipTypes: values.authorshipTypes ?? [],
                collaboratingAccountUsernames:
                    values.collaboratingAccountUsernames ?? [],
                compareDates: values.compareDates,
                countries: Object.entries(values?.countries || {}).reduce(
                    (acc, [key, value]) => {
                        if (value) acc.push(key);
                        return acc;
                    },
                    [],
                ),
                broadcastNetworks,
                broadcastNames,
                broadcastOptions,
                mvpScore,
                sort,
                order,
                dateRange,
                ageBrackets: ageBrackets ?? [],
                genders: genders ?? [],
            };

            saveFilters(saveValues);
            updateAndTrackFilters(saveValues);

            if (shouldSaveDefault) {
                sendNotifications(t('filters.defaultFiltersUpdated'));
            }
        } finally {
            if (!shouldSaveDefault) {
                setActiveKey([]);
            }
        }
    };
    const selectedFiltersElements = useMemo(() => {
        if (!selectedFilterTagsData) return [];

        let insightsLabels = [];

        if (origin === FILTER_ORIGIN.AUTO_INSIGHTS) {
            let tempInsightsTypeObj = {};
            if (
                selectedFilterTagsData.insightsIds.includes(
                    SMV_RS_BLUEPRINT_NAME,
                ) ||
                selectedFilterTagsData.insightsIds.includes(
                    SMV_RSP_BLUEPRINT_ID,
                )
            ) {
                tempInsightsTypeObj = {
                    ...tempInsightsTypeObj,
                    smv: true,
                };
            }
            if (selectedFilterTagsData.insightsIds.includes(MVP_BLUEPRINT_ID)) {
                tempInsightsTypeObj = {
                    ...tempInsightsTypeObj,
                    mvp: true,
                };
            }

            insightsLabels = INSIGHT_TYPES_OPTIONS.filter(
                ({ value }) => tempInsightsTypeObj[value],
            ).map(({ label }) => label);
        }

        const allFiltersElements = [
            {
                title: t('filters.rightsholders'),
                elements: selectedFilterTagsData.rhNames,
            },
            {
                title: t('filters.sponsors'),
                elements: selectedFilterTagsData.spNames,
            },
            {
                title: t('filters.placementType'),
                elements: selectedFilterTagsData.plTypeNames,
            },
            {
                title: t('filters.placements'),
                elements: selectedFilterTagsData.plNames,
                filterKey: 'placements',
            },
            {
                title: t('filters.from'),
                elements: selectedFilterTagsData.startDate,
                isHidden: origin == FILTER_ORIGIN.AUTO_INSIGHTS,
            },
            {
                title: t('filters.to'),
                elements: selectedFilterTagsData.endDate,
                isHidden: origin == FILTER_ORIGIN.AUTO_INSIGHTS,
            },
            {
                title: t('filters.platforms'),
                elements: selectedFilterTagsData.pltNames,
                allowedOrigins: [FILTER_ORIGIN.INSIGHTS, FILTER_ORIGIN.SMP],
            },
            {
                title: t('filters.accountTypes'),
                elements: selectedFilterTagsData.accNames,
                allowedOrigins: [FILTER_ORIGIN.INSIGHTS, FILTER_ORIGIN.SMP],
            },
            {
                title: t('filters.mediaTypes'),
                elements: selectedFilterTagsData.mediaTypesNames,
                allowedOrigins: [FILTER_ORIGIN.INSIGHTS, FILTER_ORIGIN.SMP],
            },
            {
                title: t('filters.collections'),
                elements: selectedFilterTagsData.collectionsNames,
                allowedOrigins: [
                    FILTER_ORIGIN.INSIGHTS,
                    FILTER_ORIGIN.SMP,
                    FILTER_ORIGIN.BROADCAST_EXPOSURES,
                ],
            },
            {
                title: t('filters.hashtags'),
                elements: hashtags,
                allowedOrigins: [FILTER_ORIGIN.INSIGHTS, FILTER_ORIGIN.SMP],
            },
            {
                title: t('filters.handles'),
                elements: accountUsernames,
                allowedOrigins: [FILTER_ORIGIN.INSIGHTS, FILTER_ORIGIN.SMP],
            },
            {
                title: t('filters.caption'),
                elements: captions,
                allowedOrigins: [FILTER_ORIGIN.SMP],
            },
            {
                title: t('filters.mvpScore'),
                elements: selectedFilterTagsData.mvpScoreName,
                allowedOrigins: [FILTER_ORIGIN.SMP],
            },
            {
                title: t('filters.broadcastNames'),
                elements: broadcastNames,
                allowedOrigins: [FILTER_ORIGIN.BROADCAST_EXPOSURES],
            },
            {
                title: t('filters.groupBy'),
                pluralTitle: t('filters.groupBy'),
                elements: selectedFilterTagsData.insightsGroupBy,
                allowedOrigins: [FILTER_ORIGIN.INSIGHTS],
            },
            {
                title: t('filters.comparedTo'),
                pluralTitle: t('filters.comparedTo'),
                elements: selectedFilterTagsData.compareDates,
                allowedOrigins: [FILTER_ORIGIN.INSIGHTS],
            },
            {
                title: t('filters.sortBy'),
                pluralTitle: t('filters.sortBy'),
                elements: selectedFilterTagsData.sortBy,
                allowedOrigins: [FILTER_ORIGIN.SMP],
            },
            {
                title: t('filters.insightTypes'),
                elements: insightsLabels,
                allowedOrigins: [FILTER_ORIGIN.AUTO_INSIGHTS],
            },
            {
                title: t('filters.countries'),
                elements: selectedFilterTagsData.countriesNames,
                allowedOrigins: [
                    FILTER_ORIGIN.BROADCAST_EXPOSURES,
                    FILTER_ORIGIN.INSIGHTS,
                ],
            },
            {
                title: t('filters.broadcastNetworks'),
                elements: selectedFilterTagsData.broadcastNetworksNames,
                allowedOrigins: [FILTER_ORIGIN.BROADCAST_EXPOSURES],
            },
            {
                title: t('filters.ageBracket'),
                elements: ageBrackets.map(value =>
                    t(AGE_BRACKET_LABEL_KEYS[value]),
                ),
                allowedOrigins: [FILTER_ORIGIN.BROADCAST_EXPOSURES],
            },
            {
                title: t('filters.gender'),
                elements: genders.map(value => t(GENDER_LABEL_KEYS[value])),
                allowedOrigins: [FILTER_ORIGIN.BROADCAST_EXPOSURES],
            },
            {
                title: t('filters.authorshipType.label'),
                elements: selectedFilterTagsData.authorshipTypesNames ?? [],
                allowedOrigins: [FILTER_ORIGIN.SMP],
            },
            {
                title: t('filters.collaboratingAccounts'),
                elements:
                    selectedFilterTagsData.collaboratingAccountUsernamesNames ??
                    [],
                allowedOrigins: [FILTER_ORIGIN.SMP],
            },
        ];
        return allFiltersElements.filter(
            ({ allowedOrigins }) =>
                !allowedOrigins || allowedOrigins.includes(origin),
        );
    }, [origin, selectedFilterTagsData, t]);

    const updateAndTrackFilters = updatedFilters => {
        if (!pendo) return;
        const trackObject = {};

        const usedFilters = Object.entries(updatedFilters).reduce(
            (arr, [key, val]) => {
                const isArray = Array.isArray(val);
                if (key == 'rightsholdersLeaguePairs') return arr;
                if ((isArray && val.length) || (!isArray && val)) arr.push(key);
                return arr;
            },
            [],
        );

        usedFilters.forEach(usedFilter => {
            let filterName = camelToSnakeCase(usedFilter);
            if (filterName === 'acc_own_types') filterName = 'account_types';
            if (filterName === 'platform_post_type_pairs')
                filterName = 'platform_post_types';

            let filterValues = updatedFilters[usedFilter];
            if (Array.isArray(filterValues) && filterValues.length > 3) {
                filterValues = `${filterValues.length} ${filterName}`;
            }
            if (filterName === 'start_date' || filterName === 'end_date') {
                filterValues = dayjs.isDayjs(filterValues)
                    ? filterValues.format('YYYY-MM-DD')
                    : filterValues;
            }

            trackObject[filterName] = filterValues;
        });

        if (usedFilters.length > 0) {
            pendo.track('TrackUseFilter', trackObject);
        }
        if (filters.sort !== usedFilters.sort) {
            pendo.track('TrackSortBy', { option: usedFilters.sort });
        }

        // Custom tracker for Verbal Mentions
        const selectedRightsholders = updatedFilters.rightsholders.map(
            id => rightsholders.list[id].name,
        );
        const selectedSponsors = updatedFilters.sponsors.map(
            id => sponsors.list[id].name,
        );
        const selectedPlacements =
            currentTab === BroadcastTab.VERBAL_MENTIONS
                ? 'not_available'
                : updatedFilters.placements.map(id => placements.list[id].name);
        const hasPlacementTypes = client.permissions?.includes(
            PREMIUM_PLACEMENT_TYPES,
        );
        const placement_types = hasPlacementTypes
            ? updatedFilters.placementTypes.map(
                  id => placementTypes.list[id].name,
              )
            : 'not_available';

        const shouldTrack =
            selectedRightsholders.length > 0 ||
            selectedSponsors.length > 0 ||
            placement_types.length > 0 ||
            selectedPlacements.length > 0;

        if (shouldTrack) {
            const verbalMentionsTrackObject = {
                page_name: origin,
                ...(origin === FILTER_ORIGIN.BROADCAST_EXPOSURES && {
                    currentTab,
                }),
                rightsholders: selectedRightsholders,
                sponsors: selectedSponsors,
                placement_types,
                placements: selectedPlacements,
                date_range: `${dayjs(updatedFilters.startDate).format('YYYY-MM-DD')} to ${dayjs(updatedFilters.endDate).format('YYYY-MM-DD')}`,
            };

            pendo.track(`Relo Edge::Update Filters`, verbalMentionsTrackObject);
        }
    };

    const initialInsightsIds = useMemo(() => {
        if (R.isEmpty(insightsIds)) return [];
        return insightsIds.reduce((acc, id) => {
            if (
                id === SMV_RS_BLUEPRINT_NAME ||
                (id === SMV_RSP_BLUEPRINT_ID && !acc.includes('smv'))
            ) {
                acc.push('smv');
            } else if (id === MVP_BLUEPRINT_ID && !acc.includes('mvp')) {
                acc.push('mvp');
            }
            return acc;
        }, []);
    }, [insightsIds]);

    const initialStartDate = dayjs(selectedStartDate);
    const initialEndDate = dayjs(selectedEndDate);

    const initialFilters = {
        rightsholders: rightsholders.selected,
        rightsholdersLeaguePairs: selectedRhsLeagues,
        sponsors: sponsors.selected,
        placements: placements.selected,
        placementTypes: placementTypes.selected,
        startDate: initialStartDate,
        endDate: initialEndDate,
        // A dynamic spec resolved on an earlier day no longer describes the
        // stored dates (e.g. a tab left open past midnight), so keep the dates.
        dateSpec:
            dateSpec &&
            matchesDateRange(dateSpec, initialStartDate, initialEndDate)
                ? dateSpec
                : null,
        compareDates,
        monthStart: undefined,
        monthEnd: undefined,
        platforms:
            platformPostTypePairs.selected?.reduce((acc, pltId) => {
                acc[pltId] = true;
                return acc;
            }, {}) || {},
        accTypes:
            accOwnTypes.selected?.reduce((acc, accId) => {
                acc[accId] = true;
                return acc;
            }, {}) || {},

        mediaTypes: mediaTypes.selected,
        collections: collections.selected,
        hashtags: hashtags,
        caption: captions?.[0] ?? '',
        handles: accountUsernames,
        authorshipTypes: authorshipTypes,
        collaboratingAccountUsernames: collaboratingAccountUsernames,
        mvpScore: mvpScore,
        broadcastNames: broadcastNames,
        broadcastOptions: broadcastOptions,
        countries:
            countries.selected?.reduce((acc, accId) => {
                acc[accId] = true;
                return acc;
            }, {}) || {},
        broadcastNetworks: broadcastNetworks.selected,
        ageBrackets,
        genders,
        insightsGroupBy: insightsGroupBy?.length
            ? parseStoredGroupBy(insightsGroupBy)
            : {},
        sort,
        order,
        dateRange,
        insightsIds: initialInsightsIds,
    };

    return (
        <ConfigProvider
            theme={{
                components: {
                    Collapse: {
                        headerBg: '#FAFAFA',
                        colorBorder: '#BFBFBF',
                    },
                },
            }}
        >
            <Collapse
                expandIconPosition="end"
                activeKey={activeKey}
                onChange={setActiveKey}
                className={[
                    'sticky',
                    origin === FILTER_ORIGIN.SMP
                        ? 'social-posts-filters-sticky'
                        : '',
                    origin === FILTER_ORIGIN.HOME
                        ? 'filters-sticky-layout-home'
                        : 'filters-sticky-layout-padded',
                ]
                    .filter(Boolean)
                    .join(' ')}
                ref={stickyRef}
                collapsible={shouldHideArrow ? 'icon' : 'header'}
                style={{
                    top: layoutHeaderBottom,
                    overflow: 'hidden',
                    ...(origin === FILTER_ORIGIN.BROADCAST_EXPOSURES && {
                        margin: '8px 0 14px 0',
                    }),
                }}
                expandIcon={({ isActive }) => (
                    <i
                        className={`fa-solid fa-chevron-${
                            isActive ? 'up' : 'down'
                        }`}
                        style={{ fontSize: '16px', color: '#515252' }}
                    />
                )}
                items={[
                    {
                        key: '1',
                        label: (
                            <Flex>
                                <Flex
                                    gap={12}
                                    justify="center"
                                    align="center"
                                    className="-m-r-2"
                                    style={{
                                        height: 'fit-content',
                                        fontSize: 16,
                                    }}
                                >
                                    <i className="fa-regular fa-sliders" />
                                    {t('filters.title')}
                                </Flex>
                                <Flex
                                    style={{
                                        display: 'flex',
                                        flex: 1,
                                    }}
                                >
                                    <SelectedFiltersTags
                                        selectedFiltersArray={
                                            selectedFiltersElements
                                        }
                                        overridingFiltersElement={
                                            isOverridingFilters
                                                ? overridingFiltersElement
                                                : []
                                        }
                                    />
                                </Flex>
                            </Flex>
                        ),

                        children: (
                            <Formik
                                enableReinitialize
                                initialValues={initialFilters}
                                initialStatus={{
                                    isSubmitting: false,
                                }}
                                onSubmit={handleSubmit}
                            >
                                {({ values }) => {
                                    return (
                                        <>
                                            <FiltersForm
                                                values={values}
                                                origin={origin}
                                                isOverridingFilters={
                                                    isOverridingFilters
                                                }
                                                clearOverrideFilters={
                                                    clearOverrideFilters
                                                }
                                            />
                                        </>
                                    );
                                }}
                            </Formik>
                        ),
                    },
                ]}
            />
        </ConfigProvider>
    );
};

ReFilters.propTypes = {
    saveFilters: PropTypes.func,
    getFilters: PropTypes.func,
    clearOverrideFilters: PropTypes.func,
    sendNotifications: PropTypes.func,
    filters: PropTypes.object.isRequired,
    broadcasts: PropTypes.object.isRequired,
    media: PropTypes.object,
    user: PropTypes.object.isRequired,
    origin: PropTypes.oneOf(Object.values(FILTER_ORIGIN)),
    isDefaultOpen: PropTypes.bool,
    client: PropTypes.object.isRequired,
    isOverridingFilters: PropTypes.bool,
};

export default connect(
    ['filters', 'user', 'client', 'broadcasts', 'media'],
    filterActions,
)(ReFilters);
